import { useState } from 'react';

interface InvoiceItem {
  productRef: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vatRate: number;
  totalHT: number;
}

interface CustomerInvoice {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  customerName: string;
  customerEmail: string;
  deliveryNotes: string[];
  items: InvoiceItem[];
  totalHT: number;
  totalVAT: number;
  totalTTC: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  paymentMethod?: string;
  paidAmount: number;
  remainingAmount: number;
  notes?: string;
}

// Données de démonstration
const mockInvoices: CustomerInvoice[] = [
  {
    id: '1',
    invoiceNumber: 'FC-2024-001',
    date: '2024-01-15',
    dueDate: '2024-02-14',
    customerName: 'Restaurant Le Gourmet',
    customerEmail: 'contact@legourmet.fr',
    deliveryNotes: ['BL-2024-001', 'BL-2024-002'],
    items: [
      {
        productRef: 'PROD-001',
        productName: 'Huile d\'Olive Extra Vierge 1L',
        quantity: 50,
        unitPrice: 12.50,
        discount: 5,
        vatRate: 20,
        totalHT: 593.75
      },
      {
        productRef: 'PROD-002',
        productName: 'Tomates Séchées 500g',
        quantity: 30,
        unitPrice: 8.90,
        discount: 0,
        vatRate: 5.5,
        totalHT: 267.00
      }
    ],
    totalHT: 860.75,
    totalVAT: 133.12,
    totalTTC: 993.87,
    status: 'sent',
    paidAmount: 0,
    remainingAmount: 993.87,
    notes: 'Facture regroupant les livraisons de janvier'
  },
  {
    id: '2',
    invoiceNumber: 'FC-2024-002',
    date: '2024-01-20',
    dueDate: '2024-02-19',
    customerName: 'Hôtel Bellevue',
    customerEmail: 'comptabilite@hotelbellevue.fr',
    deliveryNotes: ['BL-2024-003'],
    items: [
      {
        productRef: 'PROD-003',
        productName: 'Café Arabica Premium 1kg',
        quantity: 100,
        unitPrice: 18.50,
        discount: 10,
        vatRate: 20,
        totalHT: 1665.00
      }
    ],
    totalHT: 1665.00,
    totalVAT: 333.00,
    totalTTC: 1998.00,
    status: 'paid',
    paymentMethod: 'Virement',
    paidAmount: 1998.00,
    remainingAmount: 0
  },
  {
    id: '3',
    invoiceNumber: 'FC-2024-003',
    date: '2024-01-10',
    dueDate: '2024-02-09',
    customerName: 'Supermarché Bio Plus',
    customerEmail: 'achats@bioplus.fr',
    deliveryNotes: ['BL-2024-004', 'BL-2024-005', 'BL-2024-006'],
    items: [
      {
        productRef: 'PROD-004',
        productName: 'Miel de Lavande 500g',
        quantity: 80,
        unitPrice: 9.90,
        discount: 0,
        vatRate: 5.5,
        totalHT: 792.00
      },
      {
        productRef: 'PROD-005',
        productName: 'Confiture Fraise Bio 350g',
        quantity: 60,
        unitPrice: 5.50,
        discount: 5,
        vatRate: 5.5,
        totalHT: 313.50
      }
    ],
    totalHT: 1105.50,
    totalVAT: 60.80,
    totalTTC: 1166.30,
    status: 'overdue',
    paidAmount: 0,
    remainingAmount: 1166.30,
    notes: 'Relance effectuée le 12/02'
  },
  {
    id: '4',
    invoiceNumber: 'FC-2024-004',
    date: '2024-01-25',
    dueDate: '2024-02-24',
    customerName: 'Boulangerie Artisanale',
    customerEmail: 'contact@boulangerie-artisanale.fr',
    deliveryNotes: ['BL-2024-007'],
    items: [
      {
        productRef: 'PROD-006',
        productName: 'Farine T65 Bio 25kg',
        quantity: 40,
        unitPrice: 22.00,
        discount: 8,
        vatRate: 5.5,
        totalHT: 809.60
      }
    ],
    totalHT: 809.60,
    totalVAT: 44.53,
    totalTTC: 854.13,
    status: 'sent',
    paidAmount: 0,
    remainingAmount: 854.13
  },
  {
    id: '5',
    invoiceNumber: 'FC-2024-005',
    date: '2024-01-28',
    dueDate: '2024-02-27',
    customerName: 'Restaurant Le Gourmet',
    customerEmail: 'contact@legourmet.fr',
    deliveryNotes: ['BL-2024-008'],
    items: [
      {
        productRef: 'PROD-007',
        productName: 'Vinaigre Balsamique 500ml',
        quantity: 25,
        unitPrice: 15.90,
        discount: 0,
        vatRate: 20,
        totalHT: 397.50
      }
    ],
    totalHT: 397.50,
    totalVAT: 79.50,
    totalTTC: 477.00,
    status: 'paid',
    paymentMethod: 'Chèque',
    paidAmount: 477.00,
    remainingAmount: 0
  }
];

export default function CustomerInvoicesList() {
  const [invoices] = useState<CustomerInvoice[]>(mockInvoices);
  const [selectedInvoice, setSelectedInvoice] = useState<CustomerInvoice | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Virement');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      sent: 'Envoyée',
      paid: 'Payée',
      overdue: 'En Retard',
      cancelled: 'Annulée'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalAmount: invoices.reduce((sum, i) => sum + i.totalTTC, 0),
    paidAmount: invoices.reduce((sum, i) => sum + i.paidAmount, 0),
    remainingAmount: invoices.reduce((sum, i) => sum + i.remainingAmount, 0)
  };

  const handleRecordPayment = () => {
    if (!selectedInvoice || !paymentAmount) return;
    
    const amount = parseFloat(paymentAmount);
    if (amount <= 0 || amount > selectedInvoice.remainingAmount) {
      alert('Montant invalide');
      return;
    }

    alert(`Paiement de ${amount.toFixed(2)}€ enregistré pour la facture ${selectedInvoice.invoiceNumber}`);
    setShowPaymentModal(false);
    setPaymentAmount('');
    setSelectedInvoice(null);
  };

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Factures</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-text-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Montant Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAmount.toFixed(2)}€</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Montant Payé</p>
              <p className="text-2xl font-bold text-green-600">{stats.paidAmount.toFixed(2)}€</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Reste à Payer</p>
              <p className="text-2xl font-bold text-orange-600">{stats.remainingAmount.toFixed(2)}€</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et Recherche */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher par numéro ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes ({stats.total})
            </button>
            <button
              onClick={() => setFilterStatus('sent')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'sent'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Envoyées ({stats.sent})
            </button>
            <button
              onClick={() => setFilterStatus('paid')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'paid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Payées ({stats.paid})
            </button>
            <button
              onClick={() => setFilterStatus('overdue')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'overdue'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              En Retard ({stats.overdue})
            </button>
          </div>
        </div>
      </div>

      {/* Liste des Factures */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Numéro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Échéance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant TTC
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reste à Payer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                    <div className="text-xs text-gray-500">
                      BL: {invoice.deliveryNotes.join(', ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.customerName}</div>
                    <div className="text-xs text-gray-500">{invoice.customerEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(invoice.date).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(invoice.dueDate).toLocaleDateString('fr-FR')}
                    </div>
                    {invoice.status === 'overdue' && (
                      <div className="text-xs text-red-600 font-medium">
                        En retard
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.totalTTC.toFixed(2)}€
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {invoice.remainingAmount.toFixed(2)}€
                    </div>
                    {invoice.paidAmount > 0 && invoice.remainingAmount > 0 && (
                      <div className="text-xs text-gray-500">
                        Payé: {invoice.paidAmount.toFixed(2)}€
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Voir détails"
                      >
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                      {invoice.remainingAmount > 0 && invoice.status !== 'cancelled' && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPaymentModal(true);
                            setPaymentAmount(invoice.remainingAmount.toString());
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Enregistrer paiement"
                        >
                          <i className="ri-money-euro-circle-line text-lg"></i>
                        </button>
                      )}
                      <button
                        onClick={() => alert(`Impression de la facture ${invoice.invoiceNumber}`)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Imprimer"
                      >
                        <i className="ri-printer-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => alert(`Envoi par email à ${invoice.customerEmail}`)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Envoyer par email"
                      >
                        <i className="ri-mail-send-line text-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Détails Facture */}
      {selectedInvoice && !showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  Facture {selectedInvoice.invoiceNumber}
                </h3>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations Client */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Client</h4>
                  <p className="text-gray-700">{selectedInvoice.customerName}</p>
                  <p className="text-sm text-gray-500">{selectedInvoice.customerEmail}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Dates</h4>
                  <p className="text-sm text-gray-700">
                    Date: {new Date(selectedInvoice.date).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-sm text-gray-700">
                    Échéance: {new Date(selectedInvoice.dueDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              {/* Bons de Livraison */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Bons de Livraison</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedInvoice.deliveryNotes.map((bl, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {bl}
                    </span>
                  ))}
                </div>
              </div>

              {/* Articles */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Articles</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Référence</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Désignation</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qté</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">P.U. HT</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Remise</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">TVA</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total HT</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.productRef}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.productName}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.unitPrice.toFixed(2)}€</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.discount}%</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.vatRate}%</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{item.totalHT.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totaux */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total HT:</span>
                      <span className="font-medium text-gray-900">{selectedInvoice.totalHT.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total TVA:</span>
                      <span className="font-medium text-gray-900">{selectedInvoice.totalVAT.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                      <span className="text-gray-900">Total TTC:</span>
                      <span className="text-gray-900">{selectedInvoice.totalTTC.toFixed(2)}€</span>
                    </div>
                    {selectedInvoice.paidAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Montant Payé:</span>
                          <span className="font-medium">{selectedInvoice.paidAmount.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-orange-600">
                          <span>Reste à Payer:</span>
                          <span>{selectedInvoice.remainingAmount.toFixed(2)}€</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-700">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setSelectedInvoice(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                onClick={() => alert(`Impression de la facture ${selectedInvoice.invoiceNumber}`)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <i className="ri-printer-line mr-2"></i>
                Imprimer
              </button>
              {selectedInvoice.remainingAmount > 0 && selectedInvoice.status !== 'cancelled' && (
                <button
                  onClick={() => {
                    setShowPaymentModal(true);
                    setPaymentAmount(selectedInvoice.remainingAmount.toString());
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <i className="ri-money-euro-circle-line mr-2"></i>
                  Enregistrer Paiement
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Enregistrement Paiement */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  Enregistrer un Paiement
                </h3>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Facture:</span>
                  <span className="font-medium text-gray-900">{selectedInvoice.invoiceNumber}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Client:</span>
                  <span className="font-medium text-gray-900">{selectedInvoice.customerName}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Montant Total:</span>
                  <span className="font-medium text-gray-900">{selectedInvoice.totalTTC.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Reste à Payer:</span>
                  <span className="font-bold text-orange-600">{selectedInvoice.remainingAmount.toFixed(2)}€</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant du Paiement *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode de Paiement *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="Virement">Virement</option>
                  <option value="Chèque">Chèque</option>
                  <option value="Espèces">Espèces</option>
                  <option value="Carte">Carte Bancaire</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date du Paiement *
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentAmount('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleRecordPayment}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <i className="ri-check-line mr-2"></i>
                Enregistrer le Paiement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
